import { formatDateTime, parseUtcMs } from "@/utils/datetime";
import type { TrainingScheduleView } from "@/api/trainingSchedule";
import { describeCronExpression } from "@/modules/training/public";
import { intervalLabel } from "@/page/training/submit/scheduleDraft";
import type { TrainingScheduleRecipeSummary } from "@/api/trainingSchedule";

// 반복 주기 문구는 초안 모듈이 소유한다(요약과 목록이 같은 말을 쓴다). 여기서 다시 export 한다.
export { intervalLabel };

/**
 * 예약 실행 표시 규칙. 반복 축·상태·시각·실행 조건을 한국어 한 줄로 바꾼다.
 *
 * 상태 어휘(`active`/`paused`/`cancelled`, occurrence 의 `submitted`/`failed`/`skipped`)는 상류가
 * 소유한다. 그래서 모르는 값은 그대로 표시한다. 보기 좋게 임의로 만들면 어휘가 늘었다는 사실이
 * 화면에서 사라진다. defer 사유(`last_defer_reason`)도 자유 문장이라 번역하지 않고 원문을 표시한다.
 */

const STATUS_LABEL: Readonly<Record<string, string>> = {
  active: "활성",
  paused: "일시정지",
  cancelled: "삭제됨",
};

export function scheduleStatusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

// 스케줄 status 칩 색. 세션 status(sessionSimpleStatus)와 같은 팔레트 계열을 쓴다.
// 활성=초록(실행 중), 일시정지=호박(멈춤, 재개 가능), 삭제=회색(종료). 모르는 어휘도 회색이다.
const STATUS_CHIP_COLOR: Readonly<Record<string, number>> = {
  active: 0x16a34a,
  paused: 0xd97706,
  cancelled: 0x64748b,
};
const UNKNOWN_STATUS_CHIP_COLOR = 0x64748b;

export function scheduleStatusChipColor(status: string): number {
  return STATUS_CHIP_COLOR[status] ?? UNKNOWN_STATUS_CHIP_COLOR;
}

// 실행(occurrence) 상태. `skipped` 는 skip_if_unchanged 가 branch tip 이 바뀌지 않은 것을 보고
// 제출을 건너뛴 실행이다. 실패가 아니므로 "실패"와 다른 말이어야 한다.
const OCCURRENCE_STATUS_LABEL: Readonly<Record<string, string>> = {
  submitted: "실행됨",
  failed: "실패",
  skipped: "건너뜀",
};

export function occurrenceStatusLabel(status: string): string {
  return OCCURRENCE_STATUS_LABEL[status] ?? status;
}

// 실행 상태 칩 색. 실행됨=초록(제출됨), 실패=빨강, 건너뜀=회색(정상 회차지만 한 일이 없다).
// 모르는 어휘도 회색이다.
const OCCURRENCE_STATUS_CHIP_COLOR: Readonly<Record<string, number>> = {
  submitted: 0x16a34a,
  failed: 0xdc2626,
  skipped: 0x64748b,
};

export function occurrenceStatusChipColor(status: string): number {
  return OCCURRENCE_STATUS_CHIP_COLOR[status] ?? UNKNOWN_STATUS_CHIP_COLOR;
}

/**
 * 반복 축 한 줄. interval 과 cron 은 배타라 둘 중 하나만 값이 있다.
 *
 * cron 은 해석한 말("매일 02:00")로 표시한다. 원식과 시간대는 `recurrenceDetail` 이 툴팁용으로
 * 가진다. 해석할 수 없는 식(상류만 아는 확장)은 원식을 그대로 둔다.
 */
export function recurrenceLabel(schedule: TrainingScheduleView): string {
  if (schedule.interval_seconds != null) {
    return intervalLabel(schedule.interval_seconds);
  }
  if (schedule.cron != null && schedule.cron !== "") {
    return describeCronExpression(schedule.cron) ?? schedule.cron;
  }
  return "—";
}

// cron 원식과 시간대. 해석 뒤의 원문을 툴팁이 표시한다. interval 이거나 없으면 null 이다.
export function recurrenceDetail(schedule: TrainingScheduleView): string | null {
  if (schedule.cron == null || schedule.cron === "") {
    return null;
  }
  return `${schedule.cron} (${schedule.timezone ?? "UTC"})`;
}

// 시각 한 칸. 없으면 대시다. 다음 실행은 삭제·일시정지 상태에서 비어 있는 것이 정상이다.
export function scheduleTimeLabel(value: string | null | undefined): string {
  if (value == null || value === "") {
    return "—";
  }
  const ms = parseUtcMs(value);
  return ms === null ? "—" : formatDateTime(ms);
}

// 여유 GPU 조건 한 칸. 미설정(null/부재)은 조건 없음이다. 0 은 오지 않는다(상류 하한 1). 그래도
// 어휘를 임의로 만들지 않고 값을 그대로 표시한다.
export function minFreeGpusLabel(value: number | null | undefined): string {
  return value == null ? "조건 없음" : `여유 GPU ≥ ${value}`;
}

// 대표 dataset 의 텍스트 표기. registry 원본 이름이 있으면 그 이름, 없으면 training 쪽의 실제
// 이름, 그것도 없으면 대시다. 목록 행·편집 요약·표 셀이 같은 순서로 읽는다.
export function primaryDatasetLabel(
  recipe: TrainingScheduleRecipeSummary | null | undefined,
): string {
  const first = recipe?.datasets[0];
  if (first === undefined) {
    return "—";
  }
  return first.registry_ref?.display_name ?? first.upstream_dataset_label ?? "—";
}

// 대표 밖 입력 수. "외 N건" 의 N 이다. 대표가 없으면 0 이다.
export function extraDatasetCount(
  recipe: TrainingScheduleRecipeSummary | null | undefined,
): number {
  return Math.max(0, (recipe?.datasets.length ?? 0) - 1);
}

// 커밋 sha 축약. 목록 칸에서 40자는 불필요한 정보다. sha 가 아닌 짧은 값이 와도 자르기만 한다.
export function shortSha(sha: string | null | undefined): string {
  if (sha == null || sha === "") {
    return "—";
  }
  return sha.length > 7 ? sha.slice(0, 7) : sha;
}

// 일시정지·재개·삭제를 적용할 수 있는지. 삭제된 스케줄은 되살릴 수 없으므로 아무 명령도 받지 않는다.
export function canCommandSchedule(schedule: TrainingScheduleView): boolean {
  return schedule.status !== "cancelled";
}

// 기호를 풀어 쓴 문장. 표와 요약의 `≥` 옆 툴팁이다. 조건이 없으면 null 이다.
export function minFreeGpusSentence(value: number | null | undefined): string | null {
  return value == null ? null : `여유 GPU가 ${value}개 이상일 때만 실행합니다.`;
}
